import { LogoCard } from "@/components/common/LogoCard";
import { organisationBreadcrumbTrail } from "@/features/admin/constants/organisationNav";
import { Input } from "@patient-watch/ui/input";
import { useDeleteOrganisationLogo } from "@/features/organisations/api/deleteOrganisationLogo";
import { organisationsQueryOptions, useSelectOrganisations } from "@/features/organisations/api/selectOrganisations";
import { useUpdateOrganisationLogo } from "@/features/organisations/api/updateOrganisationLogo";
import { getSupabaseStorageUrl } from "@/lib/supabase";
import type { OrganisationT } from "@patient-watch/types";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { useNav } from "@/utils/nav";

function OrganisationLogoPage() {
    useNav(organisationBreadcrumbTrail("Logos"));

    const toastCopy = useToastCopy();
    const { data: organisations } = useSelectOrganisations();
    const updateLogoMutation = useUpdateOrganisationLogo();
    const deleteLogoMutation = useDeleteOrganisationLogo();
    const [searchQuery, setSearchQuery] = useState("");
    const [activePasteTarget, setActivePasteTarget] = useState<string | null>(null);
    const storageBaseUrl = getSupabaseStorageUrl();

    // Filter organisations based on search query
    const filteredOrganisations = useMemo(() => {
        if (!organisations) return [];

        // Sort organisations alphabetically by name
        const sortedOrganisations = [...organisations].toSorted((a, b) => {
            const nameA = a.organisation_name || "";
            const nameB = b.organisation_name || "";
            return nameA.localeCompare(nameB);
        });

        if (!searchQuery.trim()) return sortedOrganisations;

        return sortedOrganisations.filter((org) =>
            // Use optional chaining for safety
            org.organisation_name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [organisations, searchQuery]);

    // Handle clipboard paste events
    useEffect(() => {
        const handlePaste = async (e: ClipboardEvent) => {
            if (!activePasteTarget) return;

            const items = e.clipboardData?.items;
            if (!items) return;

            for (const item of items) {
                if (item.type.indexOf("image") !== -1) {
                    e.preventDefault();

                    const file = item.getAsFile();
                    if (file) {
                        // Create a new File object with a name
                        const fileName = `pasted-image-${Date.now()}.png`;
                        const newFile = new File([file], fileName, {
                            type: file.type,
                        });

                        // Upload the file
                        try {
                            await updateLogoMutation.mutateAsync({
                                organisationId: activePasteTarget,
                                file: newFile,
                            });
                            toast.success(toastCopy.logoUpdatedSuccess);
                        } catch (error) {
                            console.error("Error updating logo:", error);
                            toast.error(toastCopy.failedUpdateLogo);
                        }
                    }
                    break;
                }
            }
        };

        document.addEventListener("paste", handlePaste);
        return () => {
            document.removeEventListener("paste", handlePaste);
        };
    }, [activePasteTarget, updateLogoMutation, toastCopy]);

    const handleUpload = async (organisationId: string, file: File) => {
        try {
            await updateLogoMutation.mutateAsync({
                organisationId,
                file,
            });
        } catch (error) {
            console.error("Error updating logo:", error);
            throw error;
        }
    };

    const handleDelete = async (organisationId: string, logo_storage_path: string) => {
        try {
            await deleteLogoMutation.mutateAsync({
                organisationId,
                logo_storage_path,
            });
            toast.success(toastCopy.logoDeletedSuccess);
        } catch (error) {
            console.error("Error deleting logo:", error);
            toast.error(toastCopy.failedDeleteLogo);
        }
    };

    const handlePasteClick = (organisationId: string) => {
        setActivePasteTarget(organisationId);
        toast.info(toastCopy.pasteImageFromClipboard);
    };

    return (
        <div className="w-full min-w-0">
            <div className="mb-6">
                <div className="relative mb-6">
                    <Search className="top-1/2 left-3 absolute w-4 h-4 text-muted-foreground -translate-y-1/2 transform" />
                    <Input
                        placeholder="Search organisations..."
                        className="pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {filteredOrganisations?.map((org: OrganisationT) => {
                        // Construct logoUrl directly from org data
                        const logoUrl = org.logo_storage_path
                            ? `${storageBaseUrl}organisation-logos/${org.logo_storage_path}`
                            : null;

                        return (
                            <LogoCard
                                title={org.organisation_name}
                                key={org.organisation_id}
                                id={org.organisation_id}
                                name={org.organisation_name}
                                logoUrl={logoUrl}
                                isActive={activePasteTarget === org.organisation_id}
                                onPasteClick={handlePasteClick}
                                onUpload={handleUpload}
                                isUploading={updateLogoMutation.isPending}
                                onClick={() => setActivePasteTarget(org.organisation_id)}
                                onDelete={
                                    org.logo_storage_path
                                        ? () => handleDelete(org.organisation_id, org.logo_storage_path)
                                        : undefined
                                }
                                isDeleting={deleteLogoMutation.isPending}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_app/admin/organisation/logos")({
    component: OrganisationLogoPage,
    loader: async ({ context }) => {
        const organisations = await context.queryClient.ensureQueryData(organisationsQueryOptions());
        return { organisations };
    },
});
